/**
 * @author: Sara Nouh
 * @copyright Copyright 2020 by Radivision Inc., CA, USA. All Rights Reserved.
 * @Date: 2018-06-05
 * @description Implementation of the MarketingWidget component.
 * @filename marketing-widget.tsx
 */
import React from "react";
import LazyLoad from "react-lazyload";
import ProgressiveImage from "react-progressive-image";
import { graphql, QueryRenderer } from "react-relay";
import { ENVIRONMENT } from "../../relay/relay-environment";
import { MediaAssetFile } from "../../utilities/media-asset-file";
import { Router } from "../../utilities/router";

/**
 * The props of the MarketingWidget component,
 */
interface MarketingWidgetProps {
  /**
   * the json prop object from the backend
   */
  json: {
    /**
     * the list id
     *
     * @type {any}
     */
    sourceId?: string;
  };
}

/**
 * The state of the MarketingWidget component.
 *
 * @export
 * @interface
 */
interface MarketingWidgetState {
  /**
   * The URL of the on-star symbol.
   *
   * @type {string}
   */
  onStarSymbolUrl: string;
}

/**
 *  A React component that renders the on* Marketing panel.
 *
 * @export
 * @class MarktingSlot
 * @extends {React.Component<MarktingSlotProps>}
 */
export class MarketingWidget extends React.Component<MarketingWidgetProps, MarketingWidgetState> {
  /**
   * Constructor.
   *
   * @param {MarketingWidgetProps} props The props of the component.
   */
  constructor(props: MarketingWidgetProps) {
    super(props);
    this.state = {
      onStarSymbolUrl: `${process.env.URL_MEDIA_IMAGE}/fj/xi/FjXibsAQSjyLKcx0GhlvLA.svg`,
    };
  }

  /**
   * Returns a ReactNode containing the rendered component.
   *
   * @returns {React.ReactNode} The ReactNode containing the rendered component.
   */
  render(): React.ReactNode {
    const listId: string = this.props.json.sourceId;
    if (listId === undefined || listId === null) {
      return <div className="alert alert-danger about-danger">Error Loading Marketing Widget!</div>;
    }
    return (
      <QueryRenderer
        environment={ENVIRONMENT}
        query={graphql`
          query marketingWidgetQuery($id: ID!) {
            list(id: $id) {
              hasErrors
              errors {
                id
                location
              }
              list {
                __typename
                ... on FixedList {
                  items {
                    count
                    edges {
                      listItem {
                        item {
                          __typename
                          ... on MediaAsset {
                            id
                            label
                            landingPageUrl
                            files {
                              id
                              name
                              path
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        `}
        variables={{
          id: listId,
        }}
        render={({ error, props }) => {
          if (error) {
            return <div>{error.message}</div>;
          }
          if (props) {
            if (props.list.hasErrors) {
              return (
                <div>
                  <p>unexpected error : {JSON.stringify(props.errors)}, contact Radivision Technical team.</p>
                </div>
              );
            }
            if (props.list.list) {
              return (
                <div className="container-fluid navigationBar">
                  <div
                    className={`navigationIcons ${
                      props.list.list.items.count >= 6 ? "sixContainer" : "threeContainer"
                    }`}
                  >
                    {props.list.list.items.edges.map((edge: any, index: number) => {
                      const imageSrc = MediaAssetFile.getImageUrlFromMediaAssetFile(edge.listItem.item.files[0]);
                      const string: string = edge.listItem.item.label.substr(edge.listItem.item.label.indexOf(" ") + 1);

                      return (
                        <div
                          className={`navigationText ${props.list.list.items.count >= 6 ? "sixColums" : "threeColums"}`}
                          key={index}
                        >
                          <div
                            className="action"
                            onClick={() =>
                              edge.listItem.item.landingPageUrl !== undefined &&
                              edge.listItem.item.landingPageUrl !== null
                                ? Router.to(null, null, edge.listItem.item.landingPageUrl)
                                : null
                            }
                          >
                            {imageSrc !== null || imageSrc !== undefined ? (
                              <LazyLoad once={true}>
                                <ProgressiveImage src={imageSrc} placeholder={imageSrc}>
                                  {(src: any, LOADING: any, srcSetData: any) => (
                                    <img loading="lazy" alt="SHEconomics" src={src} />
                                  )}
                                </ProgressiveImage>
                              </LazyLoad>
                            ) : null}
                            {edge.listItem.item.label.includes("SHEconomics") ? (
                              <div>
                                <h6 className="title">SHEconomics</h6>
                                <h6 className="subTitle">{string}</h6>
                              </div>
                            ) : (
                              <h6 className="title">{edge.listItem.item.label}</h6>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            }
          }
        }}
      />
    );
  }
}
